import {
  DarkAppColors,
  LightAppColors,
  RoleThemeColors,
} from "../constants/AppColors";
import { AuthUserRole } from "../models/ResponseModels/AuthUserResponse";
import { BookingColors, ThemeMode } from "../store/slices/settingsSlice";
import { AppThemeTokens, EffectiveThemeMode } from "./themeTypes";

export const getSystemThemeMode = (): EffectiveThemeMode => {
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }

  return "light";
};

export const resolveThemeMode = (
  themeMode: ThemeMode,
  systemThemeMode: EffectiveThemeMode,
): EffectiveThemeMode => {
  return themeMode === "system" ? systemThemeMode : themeMode;
};

const getRoleColors = (
  role: AuthUserRole | null | undefined,
  mode: EffectiveThemeMode,
) => {
  if (role === "manager") {
    return RoleThemeColors[mode].manager;
  }

  if (role === "viewer") {
    return RoleThemeColors[mode].viewer;
  }

  if (role === "cleaner") {
    return RoleThemeColors[mode].cleaner;
  }

  if (role === "staff") {
    return RoleThemeColors[mode].staff;
  }

  return RoleThemeColors[mode].owner;
};

export const createThemeTokens = ({
  mode,
  role,
  bookingColors,
}: {
  mode: EffectiveThemeMode;
  role?: AuthUserRole | null;
  bookingColors: BookingColors;
}): AppThemeTokens => {
  const baseColors = mode === "dark" ? DarkAppColors : LightAppColors;
  const roleColors = getRoleColors(role, mode);

  return {
    ...baseColors,
    primary: roleColors.primary,
    primarySoft: roleColors.primarySoft,
    booking: {
      paid: bookingColors.paid,
      unpaid: bookingColors.unpaid,
      deposit: bookingColors.deposit,
      cancelled: bookingColors.cancelled,
      reserved: roleColors.primary,
    },
  };
};

export const createCssVariableMap = ({
  tokens,
  fontScale,
  mode,
}: {
  tokens: AppThemeTokens;
  fontScale: number;
  mode: EffectiveThemeMode;
}): Record<string, string> => {
  return {
    "--rp-primary": tokens.primary,
    "--rp-primary-soft": tokens.primarySoft,

    "--rp-background": tokens.background,
    "--rp-card": tokens.card,

    "--rp-surface": tokens.surface,
    "--rp-surface-soft": tokens.surfaceSoft,
    "--rp-surface-blue": tokens.surfaceBlue,

    "--rp-text-primary": tokens.textPrimary,
    "--rp-text-secondary": tokens.textSecondary,
    "--rp-text-muted": tokens.textMuted,
    "--rp-text-on-primary": tokens.textOnPrimary,

    "--rp-border": tokens.border,
    "--rp-border-soft": tokens.borderSoft,

    "--rp-danger": tokens.danger,
    "--rp-danger-soft": tokens.dangerSoft,

    "--rp-disabled": tokens.disabled,
    "--rp-disabled-background": tokens.disabledBackground,

    "--rp-property-default": tokens.propertyDefault,
    "--rp-unit-default": tokens.unitDefault,

    "--rp-booking-paid": tokens.booking.paid,
    "--rp-booking-reserved": tokens.booking.reserved,
    "--rp-booking-unpaid": tokens.booking.unpaid,
    "--rp-booking-deposit": tokens.booking.deposit,
    "--rp-booking-cancelled": tokens.booking.cancelled,

    "--rp-calendar-border": tokens.calendar.border,
    "--rp-calendar-border-soft": tokens.calendar.borderSoft,
    "--rp-calendar-header-background": tokens.calendar.headerBackground,
    "--rp-calendar-free-text": tokens.calendar.freeText,
    "--rp-calendar-selected-border": tokens.calendar.selectedBorder,

    "--rp-font-scale": `${fontScale}px`,

    colorScheme: mode,
    fontSize: `${16 + fontScale}px`,
  };
};
